// Client-side EXIF/GPS/metadata strip for image attachments (INT-04, CRY-04, T-EXIF).
// Runs BEFORE encryption so the ciphertext never carries location or
// device-identifying metadata that could deanonymize the denunciante.
//
// Conformidade (Div. 4 — POL-CMP-002 §2 / IT-CMP-001 §4): NENHUMA imagem sai do
// navegador com EXIF/GPS/metadados. Cobertura por formato, detectado por MAGIC
// BYTES (conteúdo, não MIME — MIME é spoofável e anonimato não pode depender dele):
//   - JPEG: piexif.remove (lossless — dropa todos os IFDs, incl. GPS/thumbnail).
//   - PNG:  remove os chunks de metadados (eXIf, tEXt/zTXt/iTXt, tIME) preservando
//           IHDR/PLTE/IDAT/IEND e os demais chunks de renderização. Lossless.
//   - WebP: remove os chunks EXIF/XMP do container RIFF (e limpa os flags no VP8X).
//           Lossless.
//   - HEIC/HEIF: container ISOBMFF complexo, sem stripper client-side confiável →
//           REJEITA (lança), NUNCA passa com GPS em silêncio. O upload propaga a
//           falha (attachments-upload.ts não faz catch). UX do bloqueio: ver
//           HeicNotStrippableError + proposta no PR.
//   - Qualquer outro (PDF/doc/não-imagem): pass-through inalterado.

import piexif from 'piexifjs';

/** Lançado para imagens HEIC/HEIF — não temos como remover os metadados no
 *  navegador, então bloqueamos em vez de vazar localização. */
export class HeicNotStrippableError extends Error {
  constructor() {
    super('heic_unstrippable');
    this.name = 'HeicNotStrippableError';
  }
}

type ImgFormat = 'jpeg' | 'png' | 'webp' | 'heic' | 'other';

/** Detecta o formato pelos primeiros bytes (assinatura), não pelo MIME. */
function sniff(b: Uint8Array): ImgFormat {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (
    b.length >= 8 &&
    b[0] === 0x89 &&
    b[1] === 0x50 &&
    b[2] === 0x4e &&
    b[3] === 0x47 &&
    b[4] === 0x0d &&
    b[5] === 0x0a &&
    b[6] === 0x1a &&
    b[7] === 0x0a
  ) {
    return 'png';
  }
  if (b.length >= 12 && ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') return 'webp';
  // HEIF family: ISOBMFF box `ftyp` em [4..7], marca da família HEIF em [8..11].
  if (b.length >= 12 && ascii(b, 4, 4) === 'ftyp') {
    const brand = ascii(b, 8, 4);
    if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1', 'heif'].includes(brand)) return 'heic';
  }
  return 'other';
}

function ascii(b: Uint8Array, off: number, len: number): string {
  let s = '';
  for (let i = 0; i < len; i++) s += String.fromCharCode(b[off + i]!);
  return s;
}

/**
 * Remove metadados (GPS/EXIF/etc.) de imagens. Retorna um File novo com o mesmo
 * nome/type. Não-imagens passam inalteradas. Lança HeicNotStrippableError em
 * HEIC/HEIF e propaga erro de JPEG malformado (o caller decide o fallback).
 */
export async function stripExif(file: File): Promise<File> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const fmt = sniff(bytes);

  switch (fmt) {
    case 'jpeg': {
      const dataUrl = `data:image/jpeg;base64,${bytesToBase64(bytes)}`;
      const stripped = piexif.remove(dataUrl); // lança se JPEG malformado
      return bytesToFile(base64ToBytes(afterComma(stripped)), file);
    }
    case 'png':
      return bytesToFile(stripPngChunks(bytes), file);
    case 'webp':
      return bytesToFile(stripWebpChunks(bytes), file);
    case 'heic':
      throw new HeicNotStrippableError();
    default:
      return file;
  }
}

/** Sniffa os primeiros bytes de um File — true se HEIC/HEIF. Usado pelo fluxo de
 *  anexo para decidir converter (canvas) antes de tudo. */
export async function isHeicFile(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  return sniff(head) === 'heic';
}

/**
 * Converte HEIC/HEIF → JPEG re-desenhando num canvas. O canvas só carrega PIXELS,
 * então GPS/EXIF/metadados somem POR CONSTRUÇÃO (não há passo de strip). Decodifica
 * via createImageBitmap — funciona onde o browser tem codec HEIC (Safari/iOS);
 * onde NÃO tem (Chrome/Android) `createImageBitmap` lança → o caller (Step3) dropa
 * o arquivo e avisa. NUNCA deixa HEIC passar sem conversão.
 *
 * ⚠️ Caminho feliz é browser-only (precisa de codec HEIC nativo) — não roda em
 * jsdom; validação manual/e2e no Safari. O teste cobre o FALLBACK (decode indispon.).
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file); // lança sem codec HEIC → fallback no caller
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas_2d_unavailable');
    ctx.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) throw new Error('canvas_toblob_failed');
    const name = `${file.name.replace(/\.(heic|heif)$/i, '')}.jpg`;
    return new File([blob], name, { type: 'image/jpeg' });
  } finally {
    bitmap.close?.();
  }
}

// ===== PNG — strip lossless a nível de chunk =====
// Estrutura: assinatura(8) + N chunks [length(4 BE) | type(4) | data | crc(4)].
const PNG_DROP = new Set(['eXIf', 'tEXt', 'zTXt', 'iTXt', 'tIME']);

function stripPngChunks(b: Uint8Array): Uint8Array {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const kept: Uint8Array[] = [b.subarray(0, 8)]; // assinatura
  let off = 8;
  while (off + 8 <= b.length) {
    const len = dv.getUint32(off); // big-endian
    const type = ascii(b, off + 4, 4);
    const total = 12 + len; // length + type + data + crc
    if (off + total > b.length) break; // truncado → para (não fabrica bytes)
    if (!PNG_DROP.has(type)) kept.push(b.subarray(off, off + total));
    off += total;
    if (type === 'IEND') break;
  }
  return concat(kept);
}

// ===== WebP — strip dos chunks EXIF/XMP do container RIFF =====
// Header: 'RIFF'(4) | size(4 LE) | 'WEBP'(4) | N chunks [FourCC(4) | size(4 LE) |
// data | pad(1 se size ímpar)].
const WEBP_DROP = new Set(['EXIF', 'XMP ']);

function stripWebpChunks(b: Uint8Array): Uint8Array {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const kept: Uint8Array[] = [];
  let off = 12;
  let hasVp8x = false;
  while (off + 8 <= b.length) {
    const fourcc = ascii(b, off, 4);
    const size = dv.getUint32(off + 4, true); // little-endian
    const total = 8 + size + (size & 1); // + pad byte se ímpar
    if (off + total > b.length) break;
    if (fourcc === 'VP8X') hasVp8x = true;
    if (!WEBP_DROP.has(fourcc)) kept.push(b.subarray(off, off + total));
    off += total;
  }
  const body = concat(kept);
  const out = new Uint8Array(12 + body.length);
  out.set(b.subarray(0, 4)); // 'RIFF'
  new DataView(out.buffer).setUint32(4, 4 + body.length, true); // novo size do RIFF ('WEBP' + chunks)
  out.set(b.subarray(8, 12), 8); // 'WEBP'
  out.set(body, 12);
  // Limpa os flags EXIF(0x08)/XMP(0x04) no byte de flags do VP8X (offset 20 = 12
  // header + 8 do cabeçalho do chunk). VP8X é sempre o 1º chunk quando presente.
  if (hasVp8x && out.length > 20 && ascii(out, 12, 4) === 'VP8X') {
    out[20] = out[20]! & ~0x0c;
  }
  return out;
}

// ===== helpers =====
function concat(parts: ReadonlyArray<Uint8Array>): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

function bytesToFile(bytes: Uint8Array, orig: File): File {
  // Slice explícito para ArrayBuffer (TS 6.x não widen p/ SharedArrayBuffer).
  const buf = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  return new File([buf], orig.name, { type: orig.type, lastModified: orig.lastModified });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000; // evita stack overflow em arquivos grandes
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function afterComma(dataUrl: string): string {
  const idx = dataUrl.indexOf(',');
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}
